import {
  AppBar,
  Toolbar,
  Typography,
  ListItemText,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import clsx from 'clsx';
import React, { useState, useCallback } from 'react';
import { menu } from '../../constants/data/sideNavMenu';
import { MenuList } from './components/MenuList';
import SubMenuList  from './components/SubMenuList'
import { useStyles } from './Layout.styles';
import UserProfile from '../UserProfile';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Button from "@mui/material/Button";
import logo from '../../static/images/GP_Inc_logo.png';
import { ReactComponent as CollapseIcon } from '../../static/images/collapse_menu.svg';
import { ReactComponent as ExpandIcon } from '../../static/images/expand_menu.svg';

interface Props {
  children: React.ReactNode;
}
export const Layout: React.FC<Props> = ({ children }) => {
  const drawerWidth = 340;
  const subDrawerWidth = 300;
  const [open, setOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const { classes } = useStyles();
  const theme = useTheme();
  const [subMenuData, setSubMenuData] = useState(null);
  const [menuName, setMenuName] = useState(null);

  const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  });

  const openedSubMenu = (theme: Theme) => ({
    transform: 'translateX('+ (open?drawerWidth: 65)+'px)!important',
    visibility: 'visible !important',
    width: subDrawerWidth,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  });
  
  const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
  });

  const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      }),
      ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      }),
    }),
  );

  const SubMenuDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
      width: subDrawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedSubMenu(theme),
      }),
      ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      }),
    }),
  );

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(!open);
  };

  const handleSubMenuOpen = () => {
    setOpenSubMenu(true);
  }

  const closeSubmenu = () => {
    setOpenSubMenu(false);
  }

  const handleSubMenuOpenHandle = useCallback(
    (menuName : any, subMenuList:any) => {
      setMenuName(menuName);
      setSubMenuData(subMenuList);
      setOpenSubMenu(subMenuList?true:false)
    },
    [subMenuData],
  );

  return (
    <div className={classes.layout}>
      <AppBar
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
          [classes.appBarShiftSubDrawer]: openSubMenu,
          [classes.appBarShiftDrawers]: openSubMenu && open,
        })}
        position="fixed"
        elevation={0}
      >
        <Toolbar className={classes.toolBar}>
          <Typography variant="h6" className={classes.title}>
            <img src={logo} className={classes.logoImage} alt="logo" />
          </Typography>
          <AccessTimeIcon />
          <Typography className={classes.timezone}>
            Eastern Time Zone
          </Typography>
          <hr className={classes.hr} />
          <UserProfile />
        </Toolbar>
      </AppBar>
      <Drawer 
      variant="permanent" 
      open={open}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
      sx={{
        '& .MuiDrawer-paper::before': { 
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          width: '5px',
          background: '#19477D',
          height: '100%', },
        '& .MuiDrawer-paper::after': { 
        content: '""',
        position: 'absolute',
        left: '65px',
        top: 0,
        width: '1px',
        background: '#d7dce1',
        height: '100%', },
      }}
      >
        
        <MenuList menu={menu} 
        showItemTooltip={!open} 
        onDrawerOpen={handleDrawerOpen} 
        onDrawerClose={handleDrawerClose}
        onSubMenuDrawerOpen={handleSubMenuOpen}
        handleSubMenuOpenHandle ={handleSubMenuOpenHandle}/>

        <SubMenuDrawer
          className={classes.subMenuDrawer}
          variant="persistent"
          anchor="left"
          open={openSubMenu}
          classes={{
              paper: classes.drawerPaper,
          }}
          sx={{
            '& .MuiDrawer-paper::before': {width:0},
            '& .MuiDrawer-paper::after': {width:0},
          }}
          >
            {subMenuData && 
            <div className={classes.drawerHeader}>
            <List dense={true}>
                <ListItem className={classes.subMenuListItem}>
                  <ListItemText
                    primary={<Typography variant="h6" className={classes.subMenuTitle}>{menuName}</Typography>}
                    secondary={
                    <Button variant="text" 
                    className={classes.backButton}
                    classes={{ startIcon: classes.backIcon }} 
                    startIcon={<ChevronLeft/>}
                    onClick={closeSubmenu}>
                      Collapse
                    </Button>}
                  />
                </ListItem>
            </List>
            </div>}
            {subMenuData && <SubMenuList
             subMenu={subMenuData} onClick={closeSubmenu}
           />}
        </SubMenuDrawer>
        
        <List style={{ marginTop: `auto` }} >
          <ListItem disablePadding sx={{ paddingLeft: 0, paddingRight: 0 }} 
            onClick={handleDrawerClose}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  { open ? <ExpandIcon className={classes.collapseIcon}/>
                   : <CollapseIcon className={classes.collapseIcon}/>}
                </ListItemIcon>
              </ListItemButton>
            </ListItem>
        </List>
      </Drawer>
      <main
        className={clsx({
          [classes.content]: !open,
          [classes.contentShift]: open,
          [classes.contentShiftSubDrawer]: openSubMenu,
        })}
      >
        {children}
      </main>
    </div>
  );
};
